/**
 * TIN surface processing: render buffers, contours, wireframe edges and
 * elevation/slope colouring. Everything is emitted relative to a local origin so
 * the GPU sees small float32 numbers even though survey coordinates are in the
 * millions.
 */

import type { Surface } from '../landxml/types';

export interface TinBuffers {
  /** x, y, z relative to the origin, in scene axes (x=east, y=north, z=up). */
  positions: Float32Array;
  indices: Uint32Array;
  /** Number of triangles actually emitted (invisible faces are dropped). */
  triangleCount: number;
  hiddenCount: number;
}

export function buildTin(s: Surface, ox: number, oy: number, oz: number): TinBuffers {
  const nv = s.xyz.length / 3;
  const positions = new Float32Array(nv * 3);
  for (let i = 0, j = 0; i < nv; i++, j += 3) {
    positions[j] = s.xyz[j] - ox;
    positions[j + 1] = s.xyz[j + 1] - oy;
    positions[j + 2] = s.xyz[j + 2] - oz;
  }
  let visible = 0;
  for (let t = 0; t < s.triVisible.length; t++) if (s.triVisible[t]) visible++;
  const indices = new Uint32Array(visible * 3);
  let k = 0;
  for (let t = 0, f = 0; f + 3 <= s.tris.length; t++, f += 3) {
    if (!s.triVisible[t]) continue;
    indices[k++] = s.tris[f];
    indices[k++] = s.tris[f + 1];
    indices[k++] = s.tris[f + 2];
  }
  return {
    positions, indices,
    triangleCount: visible,
    hiddenCount: s.triVisible.length - visible,
  };
}

/** Unique triangle edges, as a flat line-segment position array. */
export function tinEdges(s: Surface, ox: number, oy: number, oz: number): Float32Array {
  const seen = new Set<number>();
  const out: number[] = [];
  const key = (a: number, b: number) => (a < b ? a * 4294967296 + b : b * 4294967296 + a);
  for (let t = 0, f = 0; f + 3 <= s.tris.length; t++, f += 3) {
    if (!s.triVisible[t]) continue;
    const v = [s.tris[f], s.tris[f + 1], s.tris[f + 2]];
    for (let e = 0; e < 3; e++) {
      const a = v[e], b = v[(e + 1) % 3];
      const kk = key(a, b);
      if (seen.has(kk)) continue;
      seen.add(kk);
      out.push(
        s.xyz[a * 3] - ox, s.xyz[a * 3 + 1] - oy, s.xyz[a * 3 + 2] - oz,
        s.xyz[b * 3] - ox, s.xyz[b * 3 + 1] - oy, s.xyz[b * 3 + 2] - oz,
      );
    }
  }
  return new Float32Array(out);
}

/** Outline of the triangulation: edges used by exactly one visible triangle. */
export function tinBoundaryEdges(s: Surface, ox: number, oy: number, oz: number): Float32Array {
  const count = new Map<number, number>();
  const key = (a: number, b: number) => (a < b ? a * 4294967296 + b : b * 4294967296 + a);
  for (let t = 0, f = 0; f + 3 <= s.tris.length; t++, f += 3) {
    if (!s.triVisible[t]) continue;
    const v = [s.tris[f], s.tris[f + 1], s.tris[f + 2]];
    for (let e = 0; e < 3; e++) {
      const kk = key(v[e], v[(e + 1) % 3]);
      count.set(kk, (count.get(kk) ?? 0) + 1);
    }
  }
  const out: number[] = [];
  for (let t = 0, f = 0; f + 3 <= s.tris.length; t++, f += 3) {
    if (!s.triVisible[t]) continue;
    const v = [s.tris[f], s.tris[f + 1], s.tris[f + 2]];
    for (let e = 0; e < 3; e++) {
      const a = v[e], b = v[(e + 1) % 3];
      if (count.get(key(a, b)) !== 1) continue;
      out.push(
        s.xyz[a * 3] - ox, s.xyz[a * 3 + 1] - oy, s.xyz[a * 3 + 2] - oz,
        s.xyz[b * 3] - ox, s.xyz[b * 3 + 1] - oy, s.xyz[b * 3 + 2] - oz,
      );
    }
  }
  return new Float32Array(out);
}

// ---------------------------------------------------------------------------
// Contours
// ---------------------------------------------------------------------------

export interface ContourResult {
  minor: Float32Array;
  major: Float32Array;
  interval: number;
  majorEvery: number;
  levels: number[];
}

/**
 * Marching triangles. For a TIN this is exact: each triangle is planar, so a
 * level crosses it in a single straight segment.
 */
export function contourTin(
  s: Surface, interval: number, majorEvery: number,
  ox: number, oy: number, oz: number,
): ContourResult {
  const minor: number[] = [];
  const major: number[] = [];
  const levels: number[] = [];
  if (!(interval > 0) || !Number.isFinite(s.elevMin) || !Number.isFinite(s.elevMax)) {
    return { minor: new Float32Array(), major: new Float32Array(), interval, majorEvery, levels };
  }
  const first = Math.ceil(s.elevMin / interval) * interval;
  const last = Math.floor(s.elevMax / interval) * interval;
  const count = Math.floor((last - first) / interval) + 1;
  if (count <= 0 || count > 4000) {
    return { minor: new Float32Array(), major: new Float32Array(), interval, majorEvery, levels };
  }

  const xyz = s.xyz;
  for (let li = 0; li < count; li++) {
    const z = first + li * interval;
    levels.push(z);
    const isMajor = Math.abs(Math.round(z / (interval * majorEvery)) * (interval * majorEvery) - z) < interval * 1e-6;
    const sink = isMajor ? major : minor;
    for (let t = 0, f = 0; f + 3 <= s.tris.length; t++, f += 3) {
      if (!s.triVisible[t]) continue;
      const ia = s.tris[f] * 3, ib = s.tris[f + 1] * 3, ic = s.tris[f + 2] * 3;
      const za = xyz[ia + 2], zb = xyz[ib + 2], zc = xyz[ic + 2];
      const lo = Math.min(za, zb, zc), hi = Math.max(za, zb, zc);
      if (z < lo || z > hi) continue;
      // Collect intersections on the three edges.
      let n = 0;
      const px = [0, 0, 0]; const py = [0, 0, 0];
      const edge = (i0: number, i1: number, z0: number, z1: number) => {
        if ((z0 - z) * (z1 - z) > 0) return;
        if (z0 === z1) return;
        const u = (z - z0) / (z1 - z0);
        if (u < 0 || u > 1) return;
        px[n] = xyz[i0] + (xyz[i1] - xyz[i0]) * u;
        py[n] = xyz[i0 + 1] + (xyz[i1 + 1] - xyz[i0 + 1]) * u;
        n++;
      };
      edge(ia, ib, za, zb);
      if (n < 2) edge(ib, ic, zb, zc);
      if (n < 2) edge(ic, ia, zc, za);
      if (n < 2) continue;
      sink.push(px[0] - ox, py[0] - oy, z - oz, px[1] - ox, py[1] - oy, z - oz);
    }
  }
  return { minor: new Float32Array(minor), major: new Float32Array(major), interval, majorEvery, levels };
}

/** A sensible contour interval for the surface's relief. */
export function suggestInterval(range: number): number {
  if (!(range > 0)) return 1;
  const raw = range / 25;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (raw <= m * pow) return m * pow;
  }
  return 10 * pow;
}

// ---------------------------------------------------------------------------
// Colouring
// ---------------------------------------------------------------------------

export type Ramp = ReadonlyArray<readonly [number, number, number]>;

/** Terrain ramp: green lowlands through tan to grey/white highs. */
export const RAMP_TERRAIN: Ramp = [
  [0.24, 0.42, 0.29], [0.42, 0.55, 0.31], [0.68, 0.66, 0.40],
  [0.72, 0.56, 0.38], [0.62, 0.53, 0.51], [0.90, 0.90, 0.92],
];

/** Perceptually even blue -> yellow ramp, readable in both themes. */
export const RAMP_VIRIDIS: Ramp = [
  [0.267, 0.005, 0.329], [0.229, 0.322, 0.545], [0.128, 0.567, 0.551],
  [0.369, 0.789, 0.383], [0.993, 0.906, 0.144],
];

export function rampAt(ramp: Ramp, t: number): [number, number, number] {
  const u = Math.min(1, Math.max(0, t)) * (ramp.length - 1);
  const i = Math.min(ramp.length - 2, Math.floor(u));
  const f = u - i;
  const a = ramp[i], b = ramp[i + 1];
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
}

export function elevationColors(s: Surface, ramp: Ramp): Float32Array {
  const nv = s.xyz.length / 3;
  const out = new Float32Array(nv * 3);
  const lo = s.elevMin, hi = s.elevMax;
  const d = hi - lo || 1;
  for (let i = 0, j = 0; i < nv; i++, j += 3) {
    const c = rampAt(ramp, (s.xyz[j + 2] - lo) / d);
    out[j] = c[0]; out[j + 1] = c[1]; out[j + 2] = c[2];
  }
  return out;
}

/**
 * Per-vertex slope in degrees, area-weighted from the incident triangles - the
 * usual civil-engineering read on a surface.
 */
export function slopeDegrees(s: Surface): Float32Array {
  const nv = s.xyz.length / 3;
  const acc = new Float64Array(nv);
  const wsum = new Float64Array(nv);
  const xyz = s.xyz;
  for (let t = 0, f = 0; f + 3 <= s.tris.length; t++, f += 3) {
    if (!s.triVisible[t]) continue;
    const a = s.tris[f] * 3, b = s.tris[f + 1] * 3, c = s.tris[f + 2] * 3;
    const ux = xyz[b] - xyz[a], uy = xyz[b + 1] - xyz[a + 1], uz = xyz[b + 2] - xyz[a + 2];
    const vx = xyz[c] - xyz[a], vy = xyz[c + 1] - xyz[a + 1], vz = xyz[c + 2] - xyz[a + 2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const mag = Math.hypot(nx, ny, nz);
    if (mag < 1e-12) continue;
    const slope = (Math.acos(Math.min(1, Math.abs(nz) / mag)) * 180) / Math.PI;
    const w = mag / 2;
    for (const idx of [s.tris[f], s.tris[f + 1], s.tris[f + 2]]) {
      acc[idx] += slope * w;
      wsum[idx] += w;
    }
  }
  const out = new Float32Array(nv);
  for (let i = 0; i < nv; i++) out[i] = wsum[i] > 0 ? acc[i] / wsum[i] : 0;
  return out;
}

export function slopeColors(s: Surface, maxSlopeDeg: number, ramp: Ramp): Float32Array {
  const sl = slopeDegrees(s);
  const out = new Float32Array(sl.length * 3);
  for (let i = 0, j = 0; i < sl.length; i++, j += 3) {
    const c = rampAt(ramp, sl[i] / (maxSlopeDeg || 1));
    out[j] = c[0]; out[j + 1] = c[1]; out[j + 2] = c[2];
  }
  return out;
}

/**
 * Elevation at a plan position by locating the containing triangle.
 * Linear scan - fine for hover readout on the surfaces these files carry.
 */
export function sampleElevation(s: Surface, x: number, y: number): number | undefined {
  const xyz = s.xyz;
  for (let t = 0, f = 0; f + 3 <= s.tris.length; t++, f += 3) {
    if (!s.triVisible[t]) continue;
    const a = s.tris[f] * 3, b = s.tris[f + 1] * 3, c = s.tris[f + 2] * 3;
    const x1 = xyz[a], y1 = xyz[a + 1], x2 = xyz[b], y2 = xyz[b + 1], x3 = xyz[c], y3 = xyz[c + 1];
    const d = (y2 - y3) * (x1 - x3) + (x3 - x2) * (y1 - y3);
    if (Math.abs(d) < 1e-12) continue;
    const l1 = ((y2 - y3) * (x - x3) + (x3 - x2) * (y - y3)) / d;
    const l2 = ((y3 - y1) * (x - x3) + (x1 - x3) * (y - y3)) / d;
    const l3 = 1 - l1 - l2;
    if (l1 < -1e-9 || l2 < -1e-9 || l3 < -1e-9) continue;
    return l1 * xyz[a + 2] + l2 * xyz[b + 2] + l3 * xyz[c + 2];
  }
  return undefined;
}

/**
 * Uniform-grid index over a TIN's triangles, so plan linework that carries no
 * elevations can be draped onto the surface without an O(points x faces) scan.
 */
export class TinIndex {
  private readonly minX: number;
  private readonly minY: number;
  private readonly cell: number;
  private readonly nx: number;
  private readonly ny: number;
  private readonly buckets: Int32Array[];

  constructor(private readonly s: Surface) {
    const xyz = s.xyz;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let i = 0; i < xyz.length; i += 3) {
      if (xyz[i] < minX) minX = xyz[i];
      if (xyz[i] > maxX) maxX = xyz[i];
      if (xyz[i + 1] < minY) minY = xyz[i + 1];
      if (xyz[i + 1] > maxY) maxY = xyz[i + 1];
    }
    const faces = s.tris.length / 3;
    const target = Math.max(1, Math.min(256, Math.round(Math.sqrt(Math.max(1, faces)) / 2)));
    const w = Math.max(1e-9, maxX - minX);
    const h = Math.max(1e-9, maxY - minY);
    this.minX = minX;
    this.minY = minY;
    this.cell = Math.max(w, h) / target;
    this.nx = Math.max(1, Math.ceil(w / this.cell));
    this.ny = Math.max(1, Math.ceil(h / this.cell));

    const counts = new Int32Array(this.nx * this.ny);
    const bboxes = new Int32Array(faces * 4);
    for (let f = 0, t = 0; t < faces; t++, f += 3) {
      const a = s.tris[f] * 3, b = s.tris[f + 1] * 3, c = s.tris[f + 2] * 3;
      const x0 = Math.min(xyz[a], xyz[b], xyz[c]);
      const x1 = Math.max(xyz[a], xyz[b], xyz[c]);
      const y0 = Math.min(xyz[a + 1], xyz[b + 1], xyz[c + 1]);
      const y1 = Math.max(xyz[a + 1], xyz[b + 1], xyz[c + 1]);
      const i0 = this.clampX(x0), i1 = this.clampX(x1);
      const j0 = this.clampY(y0), j1 = this.clampY(y1);
      bboxes[t * 4] = i0; bboxes[t * 4 + 1] = i1;
      bboxes[t * 4 + 2] = j0; bboxes[t * 4 + 3] = j1;
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) counts[j * this.nx + i]++;
    }
    this.buckets = new Array(this.nx * this.ny);
    for (let k = 0; k < counts.length; k++) this.buckets[k] = new Int32Array(counts[k]);
    const fill = new Int32Array(counts.length);
    for (let t = 0; t < faces; t++) {
      const i0 = bboxes[t * 4], i1 = bboxes[t * 4 + 1];
      const j0 = bboxes[t * 4 + 2], j1 = bboxes[t * 4 + 3];
      for (let j = j0; j <= j1; j++) {
        for (let i = i0; i <= i1; i++) {
          const k = j * this.nx + i;
          this.buckets[k][fill[k]++] = t;
        }
      }
    }
  }

  private clampX(x: number): number {
    return Math.min(this.nx - 1, Math.max(0, Math.floor((x - this.minX) / this.cell)));
  }
  private clampY(y: number): number {
    return Math.min(this.ny - 1, Math.max(0, Math.floor((y - this.minY) / this.cell)));
  }

  /** Interpolated surface elevation at a plan position, or undefined outside it. */
  elevationAt(x: number, y: number): number | undefined {
    const s = this.s;
    const xyz = s.xyz;
    const list = this.buckets[this.clampY(y) * this.nx + this.clampX(x)];
    for (let n = 0; n < list.length; n++) {
      const t = list[n];
      if (!s.triVisible[t]) continue;
      const f = t * 3;
      const a = s.tris[f] * 3, b = s.tris[f + 1] * 3, c = s.tris[f + 2] * 3;
      const x1 = xyz[a], y1 = xyz[a + 1], x2 = xyz[b], y2 = xyz[b + 1], x3 = xyz[c], y3 = xyz[c + 1];
      const d = (y2 - y3) * (x1 - x3) + (x3 - x2) * (y1 - y3);
      if (Math.abs(d) < 1e-12) continue;
      const l1 = ((y2 - y3) * (x - x3) + (x3 - x2) * (y - y3)) / d;
      const l2 = ((y3 - y1) * (x - x3) + (x1 - x3) * (y - y3)) / d;
      const l3 = 1 - l1 - l2;
      if (l1 < -1e-9 || l2 < -1e-9 || l3 < -1e-9) continue;
      return l1 * xyz[a + 2] + l2 * xyz[b + 2] + l3 * xyz[c + 2];
    }
    return undefined;
  }
}
